import React, { useState } from 'react';
import {
  ClockIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  PauseIcon,
  CheckIcon,
  XMarkIcon,
  PlayIcon,
  LightBulbIcon,
  ChevronRightIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';

interface CaseActivity {
  id: string;
  activityType: string;
  previousState?: string;
  newState?: string;
  metadata?: any;
  performedAt: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface CaseProgressFlowProps {
  activities: CaseActivity[];
  currentState: string;
}

const STATE_CONFIG = {
  new: {
    label: 'New',
    icon: ClockIcon,
    gradient: 'from-blue-500 to-blue-600',
    color: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  open: {
    label: 'Open',
    icon: UserIcon,
    gradient: 'from-yellow-500 to-yellow-600',
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    bgLight: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  in_progress: {
    label: 'In Progress',
    icon: PlayIcon,
    gradient: 'from-orange-500 to-orange-600',
    color: 'bg-orange-500',
    textColor: 'text-orange-700',
    bgLight: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  awaiting_info: {
    label: 'Awaiting Info',
    icon: ChatBubbleLeftRightIcon,
    gradient: 'from-purple-500 to-purple-600',
    color: 'bg-purple-500',
    textColor: 'text-purple-700',
    bgLight: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  on_hold: {
    label: 'On Hold',
    icon: PauseIcon,
    gradient: 'from-indigo-500 to-indigo-600',
    color: 'bg-indigo-500',
    textColor: 'text-indigo-700',
    bgLight: 'bg-indigo-50',
    borderColor: 'border-indigo-200'
  },
  solution_proposed: {
    label: 'Solution Proposed',
    icon: LightBulbIcon,
    gradient: 'from-teal-500 to-teal-600',
    color: 'bg-teal-500',
    textColor: 'text-teal-700',
    bgLight: 'bg-teal-50',
    borderColor: 'border-teal-200'
  },
  closed: {
    label: 'Closed',
    icon: CheckIcon,
    gradient: 'from-green-600 to-green-700',
    color: 'bg-green-600',
    textColor: 'text-green-700',
    bgLight: 'bg-green-50',
    borderColor: 'border-green-300'
  },
  cancelled: {
    label: 'Cancelled',
    icon: XMarkIcon,
    gradient: 'from-red-500 to-red-600',
    color: 'bg-red-500',
    textColor: 'text-red-700',
    bgLight: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  auto_resolved: {
    label: 'Auto Resolved',
    icon: CheckIcon,
    gradient: 'from-emerald-500 to-emerald-600',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-700',
    bgLight: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  }
};

const CaseProgressFlow: React.FC<CaseProgressFlowProps> = ({ activities, currentState }) => {
  const [showWorkflowChart, setShowWorkflowChart] = useState(false);
  
  // Create a map of state transitions with their details
  const stateTransitions = new Map<string, { activity: CaseActivity; timestamp: string; user: string }>();
  
  // Process activities to build the flow
  activities
    .filter(activity => (activity.activityType === 'state_change' || activity.activityType === 'auto_resolved_from_alert' || activity.activityType === 'closed_from_auto_resolved_alert') && activity.newState)
    .sort((a, b) => new Date(a.performedAt).getTime() - new Date(b.performedAt).getTime())
    .forEach(activity => {
      if (activity.newState) {
        // Ensure previousState is available either directly or from metadata
        const previousState = activity.previousState || activity.metadata?.previousState;
        const activityWithPrevState = {
          ...activity,
          previousState
        };

        stateTransitions.set(activity.newState, {
          activity: activityWithPrevState,
          timestamp: new Date(activity.performedAt).toLocaleString(),
          user: `${activity.User.firstName} ${activity.User.lastName}`
        });
      }
    });

  // Build the actual flow path
  const flowPath: string[] = [];
  activities
    .filter(activity => (activity.activityType === 'state_change' || activity.activityType === 'auto_resolved_from_alert' || activity.activityType === 'closed_from_auto_resolved_alert') && activity.newState)
    .sort((a, b) => new Date(a.performedAt).getTime() - new Date(b.performedAt).getTime())
    .forEach(activity => {
      if (activity.newState && !flowPath.includes(activity.newState)) {
        flowPath.push(activity.newState);
      }
    });

  // If no activities yet, show just the current state
  if (flowPath.length === 0) {
    flowPath.push(currentState);
  }

  const renderStateStep = (state: string, index: number) => {
    const config = STATE_CONFIG[state as keyof typeof STATE_CONFIG];
    if (!config) return null;

    const Icon = config.icon;
    const transition = stateTransitions.get(state);
    const isActive = state === currentState;
    const isCompleted = transition && state !== currentState;
    const isCancelled = currentState === 'cancelled';

    return (
      <div key={state} className="flex flex-col items-center group relative" style={{ marginTop: '7px' }}>
        {/* State Circle with professional styling */}
        <div
          className={`
            relative flex items-center justify-center w-16 h-16 rounded-full border-3 transition-all duration-300 mb-3 shadow-lg
            ${isActive
              ? `bg-gradient-to-br ${config.gradient} text-white border-white shadow-xl scale-110 ring-4 ring-white ring-opacity-30`
              : isCompleted
                ? (isCancelled ? 'bg-white text-gray-400 border-gray-200 shadow-sm' : `bg-gradient-to-br ${config.gradient} text-white border-white shadow-md`)
                : `bg-white text-gray-400 border-gray-200 shadow-sm hover:border-gray-300`
            }
            group-hover:scale-105 group-hover:shadow-xl cursor-pointer transform-gpu
          `}
        >
          <Icon className="w-6 h-6" />
          
          {/* Status indicator dot for active state */}
          {isActive && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          )}

          {/* Completion checkmark - don't show for cancelled state or when workflow is cancelled */}
          {isCompleted && !isActive && state !== 'cancelled' && !isCancelled && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
              <CheckIcon className="w-3 h-3 text-white" />
            </div>
          )}
          
          {/* Simple Tooltip */}
          <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
            <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-lg whitespace-nowrap">
              {transition ? (
                <div>
                  <div>Changed by: {transition.user}</div>
                  <div>On: {transition.timestamp}</div>
                </div>
              ) : (
                <div>
                  {isActive ? 'Current state' : 
                   isCompleted ? 'Completed' : 
                   'Not reached'}
                </div>
              )}
            </div>
            {/* Tooltip arrow */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
          </div>
        </div>

        {/* Professional State Label */}
        <div className={`text-sm font-semibold text-center max-w-24 leading-tight ${
          isActive ? config.textColor : isCompleted ? config.textColor : 'text-gray-500'
        }`}>
          {config.label}
        </div>

        {/* Professional Arrow Connection */}
        {index < flowPath.length - 1 && (
          <div className="absolute top-8 left-20 transform -translate-y-1/2 z-0">
            <div className={`flex items-center transition-all duration-300 ${
              isCompleted ? 'text-gray-400' : 'text-gray-300'
            }`}>
              <div className="w-8 h-0.5 bg-current"></div>
              <ChevronRightIcon className="w-5 h-5 ml-1" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWorkflowChart = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-auto max-w-[85vw] h-auto max-h-[90vh] overflow-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900">Case Workflow Flowchart</h3>
            <button
              onClick={() => setShowWorkflowChart(false)}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-auto flex-1 flex flex-col items-center justify-center">
            {/* Case Workflow Image */}
            <div className="max-w-full max-h-full">
              <img 
                src="/Case_Workflow.png" 
                alt="Case Workflow Flowchart"
                className="max-w-full max-h-[70vh] object-contain"
                onError={(e) => {
                  // Fallback if image doesn't load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentNode as HTMLElement;
                  // nosec: innerHTML with static string - no user input, safe fallback UI for missing image
                  parent.innerHTML = `
                    <div class="flex flex-col items-center justify-center p-12 text-gray-500">
                      <div class="text-6xl mb-4">📊</div>
                      <h4 class="text-xl font-medium mb-2">Workflow Chart</h4>
                      <p class="text-center">Case_Workflow.png not found.<br/>Please add the image to the public folder.</p>
                    </div>
                  `;
                }}
              />
            </div>
            
            {/* Bottom Note */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 font-medium">
                Case can be marked as Cancelled anytime
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-8 shadow-lg border border-gray-100">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">Case Progress Flow</h3>
          <p className="text-sm text-gray-500">Track the journey of this case through its lifecycle</p>
        </div>
        <button
          onClick={() => setShowWorkflowChart(true)}
          className="group relative p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 hover:shadow-md"
          title="View Complete Workflow Chart"
        >
          <DocumentChartBarIcon className="w-6 h-6" />
          <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Workflow Chart
          </span>
        </button>
      </div>
      
      {flowPath.length > 0 ? (
        <div className="relative">
          <div className="flex items-start justify-center space-x-12 overflow-x-auto pb-8 px-2" style={{ marginTop: '7px' }}>
            {flowPath.map((state, index) => renderStateStep(state, index))}
          </div>
          
          {/* Professional background pattern */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="w-full h-full" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='60' cy='60' r='8'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}></div>
          </div>
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="relative">
            <ClockIcon className="w-16 h-16 text-gray-300 mx-auto mb-6" />
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            </div>
          </div>
          <h4 className="text-xl font-medium text-gray-600 mb-3">Case Journey Begins</h4>
          <p className="text-gray-500 max-w-md mx-auto">Track the complete lifecycle of this case from creation to resolution. State transitions and progress updates will be displayed here.</p>
          
          {/* Preview of potential states */}
          <div className="mt-8 flex justify-center items-center space-x-3 opacity-40">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <ClockIcon className="w-4 h-4 text-white" />
            </div>
            <div className="w-4 h-0.5 bg-gray-300"></div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-white" />
            </div>
            <div className="w-4 h-0.5 bg-gray-300"></div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <PlayIcon className="w-4 h-4 text-white" />
            </div>
            <div className="text-gray-400 text-xs">...</div>
          </div>
        </div>
      )}
      
      
      {showWorkflowChart && renderWorkflowChart()}
    </div>
  );
};

export default CaseProgressFlow;